import React, { useState, useRef } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { auth, db } from '../firebase';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '../components/ds/core/Button';
import { Input } from '../components/ds/core/Input';
import './LoginView.css';

function LoginView() {
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const recaptchaRef = useRef(null);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  };

  // Deliver any pending world messages to this newly registered user
  const deliverPendingMessages = async (newUserId) => {
    try {
      const pendingQuery = query(
        collection(db, 'pendingWorldMessages'),
        where('status', '==', 'pending'),
        where('senderId', '!=', newUserId),
        orderBy('createdAt', 'asc'),
        limit(1)
      );
      const pendingSnapshot = await getDocs(pendingQuery);

      if (pendingSnapshot.empty) return;

      const pendingDoc = pendingSnapshot.docs[0];
      const pending = pendingDoc.data();

      // Create conversation between sender and new user
      const conversationData = {
        participants: [pending.senderId, newUserId],
        lastMessage: pending.text,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      const conversationRef = await addDoc(collection(db, 'conversations'), conversationData);

      // Create the message
      const messageData = {
        conversationId: conversationRef.id,
        senderId: pending.senderId,
        receiverId: newUserId,
        text: pending.text,
        timestamp: serverTimestamp(),
        read: false
      };
      if (pending.senderCountryCode) {
        messageData.senderCountryCode = pending.senderCountryCode;
      }
      await addDoc(collection(db, 'messages'), messageData);

      // Mark pending message as delivered
      await updateDoc(doc(db, 'pendingWorldMessages', pendingDoc.id), {
        status: 'delivered',
        deliveredTo: newUserId,
        deliveredAt: serverTimestamp()
      });
    } catch (err) {
      // Non-critical - don't block registration if delivery fails
      console.error('Error delivering pending message:', err);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!email.trim() || !username.trim() || !password.trim() || !birthDate.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!/^[\p{L}\p{N} ._-]{2,20}$/u.test(username.trim())) {
      setError('Username must be 2-20 characters: letters, numbers, spaces, and . _ - only');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Check age requirement
    const age = calculateAge(birthDate);
    if (age < 18) {
      window.location.href = 'https://www.google.com/search?q=adult&oq=adult&gs_lcrp=EgRlZGdlKgYIABBFGDkyBggAEEUYOTIKCAEQABixAxiABDIKCAIQABixAxiABDIHCAMQABiABDIKCAQQABixAxiABDIHCAUQABiABDIHCAYQABiABDIHCAcQABiABDIKCAgQABixAxiABNIBCDE1NTZqMGoxqAIAsAIA&sourceid=chrome&ie=UTF-8';
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Check terms acceptance
    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions to create an account');
      return;
    }

    // Check reCAPTCHA
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get user's country code from IP geolocation
      let countryCode = null;
      try {
        const geoResponse = await fetch('https://ipapi.co/json/');
        const geoData = await geoResponse.json();
        countryCode = geoData.country_code || null;
      } catch (geoError) {
        console.error('Failed to get country code:', geoError);
      }

      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;

      // Create user document in Firestore (no PII - readable by all authenticated users)
      // Defaults to the Classic skin (starfield triggers on #0f1423 background)
      await setDoc(doc(db, 'users', user.uid), {
        username: username.trim(),
        isGuest: false,
        createdAt: new Date().toISOString(),
        themeBackgroundColor: '#0f1423',
        themeEarthColor: '#2faf68',
        themeOceanColor: '#145fb0',
        themeNavbarColor: '#001F3F',
        themeSkin: 'daylight',
        countryCode: countryCode,
        acceptedTermsAt: new Date().toISOString()
      });

      // Store PII separately - readable only by the owner and staff
      await setDoc(doc(db, 'userPrivate', user.uid), {
        email: email.trim(),
        birthDate: birthDate
      });

      // Deliver any queued world messages to this new user
      await deliverPendingMessages(user.uid);

      localStorage.setItem('userId', user.uid);
      localStorage.setItem('userNickname', username.trim());
      localStorage.setItem('isGuest', 'false');

      window.location.reload();
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Email already in use');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError(err.message || 'Failed to create account');
      }
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;

      // Get user document from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // If user doesn't have a country code, add it
        if (!userData.countryCode) {
          try {
            const geoResponse = await fetch('https://ipapi.co/json/');
            const geoData = await geoResponse.json();
            const countryCode = geoData.country_code || null;

            if (countryCode) {
              await updateDoc(doc(db, 'users', user.uid), {
                countryCode: countryCode
              });
            }
          } catch (geoError) {
            console.error('Failed to update country code:', geoError);
          }
        }

        localStorage.setItem('userId', user.uid);
        localStorage.setItem('userNickname', userData.username || 'User');
        localStorage.setItem('isGuest', 'false');
        window.location.reload();
      } else {
        setError('User data not found');
      }
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError(err.message || 'Failed to login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setMessage('');
    if (!email.trim()) {
      setError('Enter your email above first, then click "Reset password".');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMessage('Password reset email sent! Check your inbox for a link to reset your password.');
    } catch (err) {
      if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        // Neutral message so we don't reveal whether the email is registered
        setMessage('If an account exists for that email, a password reset link has been sent.');
      }
    }
  };

  return (
    <div className="login-page">
      {/* Left Side - Brand illustration / marketing */}
      <div className="login-left">
        <div className="login-brand">
          <div className="login-logo">
            <span className="login-logo-mark">🌐</span>
            Yapp
          </div>
          <p className="login-tagline">
            Send a note across the planet. Stay anonymous until you become friends.
          </p>
          <img
            src="/yap-friendship.png"
            alt="Two strangers become friends through Yapp"
            className="login-image"
          />
          <ul className="login-feature-list">
            <li>Send messages to random users worldwide</li>
            <li>Stay anonymous until you decide to connect</li>
            <li>Make friends and reveal your identity</li>
            <li>Customize your experience with themes</li>
          </ul>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="login-middle">
        <div className="login-card">
          <div className="login-header">
            <h1>{isCreatingAccount ? 'Create account' : 'Welcome back'}</h1>
            <p>
              {isCreatingAccount
                ? 'Join Yapp — it takes a few seconds.'
                : 'Log in to keep yapping with the world.'}
            </p>
          </div>

          <form className="login-form">
            <Input
              label="Email"
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              icon={<span>✉️</span>}
            />

            {isCreatingAccount && (
              <Input
                label="Username"
                id="username"
                type="text"
                placeholder="Choose a username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                icon={<span>@</span>}
              />
            )}

            <Input
              label="Password"
              id="password"
              type="password"
              placeholder={isCreatingAccount ? "Enter your password (min 6 characters)" : "Enter your password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              icon={<span>🔒</span>}
            />

            {isCreatingAccount && (
              <Input
                label="Birth Date (Must be 18+)"
                id="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                disabled={loading}
              />
            )}

            {isCreatingAccount && (
              <div className="form-group" style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Le1MTcsAAAAAMamCc5kDVcrziWn5GikniKzbr-R"
                />
              </div>
            )}

            {isCreatingAccount && (
              <div className="terms-checkbox-container">
                <label className="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    disabled={loading}
                  />
                  <span>
                    I agree to the{' '}
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => setShowTermsModal(true)}
                    >
                      Terms and Conditions
                    </button>
                    {' '}and{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="terms-link"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
            {message && <div className="error-message" style={{ background: '#e8f5e9', color: '#2e7d32', borderColor: '#a5d6a7' }}>{message}</div>}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              onClick={isCreatingAccount ? handleCreateAccount : handleLogin}
              disabled={loading}
              style={{ marginTop: '1rem' }}
            >
              {loading ? 'Loading...' : (isCreatingAccount ? 'Create Account' : 'Login')}
            </Button>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  setIsCreatingAccount(!isCreatingAccount);
                  setError('');
                  setMessage('');
                  setAcceptedTerms(false);
                }}
                className="login-switch-link"
              >
                {isCreatingAccount ? 'Already have an account? Login' : "Don't have an account? Create one"}
              </button>
            </div>

            {!isCreatingAccount && (
              <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="login-switch-link"
                  disabled={loading}
                >
                  Reset password
                </button>
              </div>
            )}

          </form>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
            <a href="/about" target="_blank" rel="noopener noreferrer" className="login-switch-link">About Yapp</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="login-switch-link">Privacy Policy</a>
            <a href="/contact" target="_blank" rel="noopener noreferrer" className="login-switch-link">Contact Us</a>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <button className="terms-modal-close" onClick={() => setShowTermsModal(false)}>×</button>
            <h2>Terms and Conditions</h2>
            <p className="terms-effective-date">Effective Date: January 19, 2026</p>

            <div className="terms-content">
              <p>
                Welcome to Yapp. By creating an account and using our services, you agree to be bound by these
                Terms and Conditions. Please read them carefully before using the platform.
              </p>

              <h3>1. Acceptance of Terms</h3>
              <p>
                By accessing or using Yapp, you confirm that you are at least 18 years of age and agree to
                comply with these Terms and Conditions. If you do not agree with any part of these terms,
                you must not use our services.
              </p>

              <h3>2. Description of Service</h3>
              <p>
                Yapp is a messaging platform that allows users to send and receive messages
                with other users worldwide. While Yapp allows anonymous interaction between users
                (meaning other users cannot see your personal information unless you choose to share it),
                we may collect certain information to operate the service, ensure safety, and comply with
                legal obligations. The service is provided "as is" without warranties of any kind.
              </p>

              <h3>3. User Accounts</h3>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for
                all activities that occur under your account. You agree to provide accurate and complete
                information when creating your account.
              </p>

              <h3>4. Acceptable Use Policy</h3>
              <p>You agree NOT to use Yapp to:</p>
              <ul>
                <li>Send harassing, threatening, defamatory, or abusive messages</li>
                <li>Share illegal content, including but not limited to child exploitation material</li>
                <li>Engage in spam, phishing, or fraudulent activities</li>
                <li>Impersonate other individuals or entities</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Share personal information of others without their consent</li>
                <li>Attempt to circumvent security measures or exploit vulnerabilities</li>
              </ul>

              <h3>5. Content and Conduct</h3>
              <p>
                You are solely responsible for the content you share on Yapp. We reserve the right to
                remove any content and suspend or terminate accounts that violate these terms without
                prior notice. We do not have an obligation to monitor user content but may do so at our
                discretion to ensure compliance with these terms and applicable laws.
              </p>

              <h3>6. Privacy</h3>
              <p>
                Your use of Yapp is also governed by our Privacy Policy. We collect and process personal
                data in accordance with applicable privacy laws, including Quebec's Act Respecting the
                Protection of Personal Information in the Private Sector and Canada's Personal Information
                Protection and Electronic Documents Act (PIPEDA).
              </p>

              <h3>7. Data Collection</h3>
              <p>We may collect the following information:</p>
              <ul>
                <li>Email address and username</li>
                <li>Date of birth (for age verification)</li>
                <li>IP address and approximate location (country)</li>
                <li>Messages sent through the platform</li>
                <li>Usage data and analytics</li>
              </ul>

              <h3>8. Intellectual Property</h3>
              <p>
                All content, features, and functionality of Yapp are owned by us and are protected by
                international copyright, trademark, and other intellectual property laws.
              </p>

              <h3>9. Limitation of Liability</h3>
              <p>
                To the maximum extent permitted by law, Yapp and its operators shall not be liable for
                any indirect, incidental, special, consequential, or punitive damages arising from your
                use of the service. We are not responsible for the content or conduct of other users.
              </p>

              <h3>10. Indemnification</h3>
              <p>
                You agree to indemnify and hold harmless Yapp and its operators from any claims, damages,
                losses, or expenses arising from your use of the service or violation of these terms.
              </p>

              <h3>11. Termination</h3>
              <p>
                To the extent permitted by applicable law, we reserve the right to suspend or terminate
                your account at any time, with or without cause. Upon termination, your right to use
                Yapp will immediately cease.
              </p>

              <h3>12. Modifications to Terms</h3>
              <p>
                We may modify these Terms and Conditions at any time. Continued use of Yapp after
                changes constitutes acceptance of the modified terms. We will make reasonable efforts
                to notify users of significant changes.
              </p>

              <h3>13. Governing Law</h3>
              <p>
                These Terms and Conditions are governed by the laws of the Province of Quebec and the
                federal laws of Canada applicable therein. Any disputes shall be resolved in the courts
                of Montreal, Quebec.
              </p>

              <h3>14. Severability</h3>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions
                will continue in full force and effect.
              </p>

              <h3>15. Contact Information</h3>
              <p>
                If you have any questions about these Terms and Conditions, please contact us at
                contact@yapp.moe.
              </p>

              <p className="terms-agreement-note">
                By creating an account, you acknowledge that you have read, understood, and agree to
                be bound by these Terms and Conditions.
              </p>
            </div>

            <button
              className="terms-accept-btn"
              onClick={() => {
                setAcceptedTerms(true);
                setShowTermsModal(false);
              }}
            >
              I Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginView;
